import { useState, type ComponentProps } from 'react';
import { AppHeader } from '@/features/approvals/components/AppHeader';
import { AudienceToggle } from '@/features/approvals/components/AudienceToggle';
import { BulkApproveBar } from '@/features/approvals/components/BulkApproveBar';
import { GroupTabs } from '@/features/approvals/components/GroupTabs';
import { Pagination } from '@/features/approvals/components/Pagination';
import { ParticipantCards } from '@/features/approvals/components/ParticipantCards';
import { ParticipantsTable } from '@/features/approvals/components/ParticipantsTable';
import { TableSkeleton } from '@/features/approvals/components/TableSkeleton';
import { useApprovals } from '@/features/approvals/useApprovals';
import { BULK_APPROVE } from '@/shared/config/event.config';
import { useEventConfig } from '@/shared/config/event-config-context';
import { DESKTOP_QUERY, useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { pluralize } from '@/shared/lib/datetime';
import { Button } from '@/shared/ui/Button';
import { Card, CardFooter } from '@/shared/ui/Card';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorState } from '@/shared/ui/ErrorState';
import { InboxIcon, RefreshIcon } from '@/shared/ui/icons';

type ListViewProps = ComponentProps<typeof ParticipantsTable>;

/** Escolhe a visualização conforme o espaço disponível: tabela ou cartões. */
function ListView(props: ListViewProps) {
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  return isDesktop ? <ParticipantsTable {...props} /> : <ParticipantCards {...props} />;
}

/** Tela principal: filtros, listagem e ações de aprovação. */
export function ApprovalsPage() {
  const approvals = useApprovals();
  const { groups, profiles } = useEventConfig();
  const [confirmingBulk, setConfirmingBulk] = useState(false);

  // Rótulos da parametrização; o fallback cobre o instante entre trocar de filtro e
  // a lista nova chegar, e um documento que traga status sem nome.
  const activeGroupLabel = groups.find(group => group.key === approvals.group)?.label ?? approvals.group;
  const activeProfileLabel = profiles.find(profile => profile.name === approvals.profile)?.label ?? approvals.profile;
  const hasParticipants = approvals.participants.length > 0;
  // A ação em lote só aparece no grupo declarado na parametrização.
  const showBulkBar = BULK_APPROVE.enabled && approvals.group === BULK_APPROVE.fromGroup && hasParticipants && !approvals.loading;

  const handleConfirmBulk = async () => {
    await approvals.approveAllOnPage();
    setConfirmingBulk(false);
  };

  return (
    <div className="min-h-dvh">
      <AppHeader />

      <main className="mx-auto max-w-[85rem] px-4 py-6 sm:px-6 sm:py-8">
        <div className="mb-6 flex flex-col gap-4 sm:mb-7">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="space-y-1">
              <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-[1.375rem]">Inscrições</h1>
              <p className="text-sm text-ink-muted">
                {/* {EVENT.fullTitle} */}
                {/* {activeProfileLabel ? `${activeProfileLabel}` : ''} · {activeGroupLabel.toLowerCase()} */}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <AudienceToggle active={approvals.profile} onChange={approvals.changeProfile} />
              <Button variant="secondary" size="sm" onClick={approvals.reload} loading={approvals.loading || approvals.refreshing}>
                <RefreshIcon className="size-4" />
                <span className="hidden sm:inline">Atualizar</span>
              </Button>
            </div>
          </div>

          <GroupTabs active={approvals.group} activeCount={approvals.error ? null : approvals.totalRecords} onChange={approvals.changeGroup} />
        </div>

        <Card>
          {showBulkBar ? (
            <BulkApproveBar pageCount={approvals.participants.length} running={approvals.bulkRunning} onApproveAll={() => setConfirmingBulk(true)} />
          ) : null}

          {approvals.error ? (
            <ErrorState message={approvals.error} onRetry={approvals.reload} />
          ) : approvals.loading ? (
            <TableSkeleton />
          ) : hasParticipants ? (
            <ListView
              participants={approvals.participants}
              currentGroup={approvals.group}
              pendingIds={approvals.pendingIds}
              onMove={approvals.move}
            />
          ) : (
            <EmptyState
              icon={<InboxIcon />}
              title="Nenhuma inscrição aqui"
              description={
                activeProfileLabel
                  ? `Não há participantes em "${activeGroupLabel.toLowerCase()}" na trilha ${activeProfileLabel}.`
                  : `Não há participantes em "${activeGroupLabel.toLowerCase()}".`
              }
            />
          )}

          {!approvals.error ? (
            <CardFooter>
              <Pagination
                page={approvals.page}
                totalPages={approvals.totalPages}
                range={approvals.range}
                totalRecords={approvals.totalRecords}
                disabled={approvals.loading}
                onChange={approvals.goToPage}
              />
            </CardFooter>
          ) : null}
        </Card>
      </main>

      {/* Montado só quando aberto: o conteúdo de um <dialog> fechado continua no
          DOM e permanece alcançável por buscas e leitores de tela. */}
      {confirmingBulk ? (
        <ConfirmDialog
          open
          title="Aprovar todas desta página?"
          description={`${pluralize(approvals.participants.length, 'inscrição será aprovada', 'inscrições serão aprovadas')} e cada participante receberá o e-mail de confirmação. A ação não pode ser desfeita em lote.`}
          confirmLabel="Aprovar todas"
          loading={approvals.bulkRunning}
          onConfirm={handleConfirmBulk}
          onCancel={() => setConfirmingBulk(false)}
        />
      ) : null}
    </div>
  );
}
